import difference from 'lodash/difference';
import { IClientVarsService } from '../../../core/interfaces/client-vars-service.interface';
import RequestManager, { IRequestManagerResponse } from './request-manager';
import { IUserManager } from '../core/interfaces/user-manager.interface';
import { getRealUid } from './user-manager';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { ICooperationLockManager, ISlideLock } from '../core/interfaces/cooperation-lock-manager.interface';
import { IEventManager } from '@tencent/slide-sdk-model';
import { IPresentationStoreService } from '../../../core/interfaces/presentation-service.interface';
import { EditEvents, IEditEvents } from '../../../core/interfaces/event.interface';
import { ICollaborationManager } from '@tencent/slide-sdk-model/src/manager/collaboration-manager';

/** 服务端下发的全量 lock 信息 */
interface ServerLockMap {
    [slideId: string]: {
        /** 注意这个字段实际上是 uid */
        userId: string;
    };
}

type CMD = 'GET_FULL_LOCK' | 'UNLOCK' | 'LOCK';
const TIME_INTERVAL_30_MIN = 30 * 60 * 1000;
const MODULE_NAME = 'CooperationLockV3';

/**
 * 协同锁管理
 * - 当前 ppt 仅支持 **页面级别的锁**，以下的所有 ’锁‘ 概念均围绕 页面id(slideId) or 页数(slideIndex)
 * - 用户必须具有 [写权限] 才能 *查询* or 操作 锁
 * - 一个用户最多持有一个页面锁
 */
class CooperationLockManager implements ICooperationLockManager {
    private static TIMEOUT_LOCK_CHECK_INTERVAL_MIN = 60000;
    private static TIMEOUT_LOCK_CHECK_INTERVAL_MAX = 60000 * 5;
    private static UNLOCK_INTERVAL = 60000;

    private uid?: string;
    private requestManager: RequestManager<CMD>;
    /** 服务端下发的全量锁信息 */
    private serverLockMap: ServerLockMap = {};
    /** 轮询 allLockInfo 以检查过期锁, 默认一分钟 */
    private pollingAllLockInfoTimer: NodeJS.Timer | null;
    /** 延迟解锁 timer，默认一分钟 */
    private unlockTimer: NodeJS.Timer | null;

    public constructor(
        private clientVars: IClientVarsService,
        private presentationStoreService: IPresentationStoreService,
        private userManager: IUserManager,
        private collaborationManager: ICollaborationManager,
        private event: IEditEvents,
        private sdkEventManager: IEventManager,
    ) {
        this.requestManager = new RequestManager<CMD>(
            this.logger,
            MODULE_NAME,
            this.collaborationManager,
        );
        const userId = this.clientVars.getClientVars().userId ?? '';
        if (userId) this.uid = getRealUid(userId);

        this.sdkEventManager.socketEvents.onChangeToOnline(this.getAllLockInfo.bind(this));
        this.sdkEventManager.socketEvents.onUserLeave(this.onUserLeave.bind(this));
        this.sdkEventManager.socketEvents.onUserEnter(this.onUserLeave.bind(this));
        this.sdkEventManager.socketEvents.onAllLockInfo(this.onLock.bind(this));
        this.sdkEventManager.socketEvents.onUnlockInfo(this.onUnlock.bind(this));
    }

    /**
     * 根据 slide/layout/master id 加锁
     * @param id
     * @returns
     */
    public async lockById(id: string): Promise<IRequestManagerResponse> {
        const lock = this.serverLockMap[id];
        if (lock) {
            this.logger.info(`${MODULE_NAME} lockById(${id}) failed, reason: been locked`);
            return RequestManager.makeResponse(false, {
                message: `the page has been locked by ${lock.userId === this.uid ? 'you' : 'other'}`,
            });
        }

        return this.requestManager.send('LOCK', {}, {}, 0, { pageid: id });
    }

    /**
     * 根据 slide/layout/master index 加锁
     * @param index
     * @param type
     * @returns
     */
    public async lockByIndex(index: number, type: UIModelType): Promise<IRequestManagerResponse> {
        const pageId = this.getListByType(type)[index]?.id;
        if (!pageId) {
            this.logger.info(`${MODULE_NAME} lockByIndex(${index}) failed, reason: not found`);
            return RequestManager.makeResponse(false, { message: `index ${index} is not found` });
        }

        return this.lockById(pageId);
    }

    /**
     * 根据 slide/layout/master id 解锁
     * @param id
     * @returns
     */
    public unlockById(id: string) {
        // 不校验本地数据中 锁是否存在

        // 锁已存在但不是自己持有时抛错
        const lock = this.serverLockMap[id];
        if (lock && lock.userId !== this.uid) {
            this.logger.info(`${MODULE_NAME} unlockById(${id}) failed, reason: been locked by others`);
            return RequestManager.makeResponse(false, { message: 'cannot unlock slide locked by others' });
        }

        return this.requestManager.send('UNLOCK', {}, {}, 0, { pageid: id });
    }

    /**
     * 根据 slide/layout/master index 解锁
     * @param index
     * @param type
     * @returns
     */
    public async unlockByIndex(index: number, type: UIModelType) {
        const pageId = this.getListByType(type)[index]?.id;
        if (!pageId) {
            this.logger.info(`${MODULE_NAME} unlockByIndex(${index}) failed, reason: not found`);
            return RequestManager.makeResponse(false, { message: `slideIndex ${index} is not found` });
        }

        return this.unlockById(pageId);
    }

    /** 获取所有加锁信息 */
    public get lockInfo(): ISlideLock[] {
        return Object.keys(this.serverLockMap)
            .map(slideId => this.getLockInfoById(slideId, this.getTypeById(slideId)))
            .filter((lock): lock is ISlideLock => lock !== undefined);
    }

    /** 我的加锁的信息 */
    public get myLockInfo(): ISlideLock | undefined {
        if (!this.uid) return;

        return this.getLockInfoByUid(this.uid);
    }

    /**
     * 一段时间后解锁，每次调用重新计时
     * @param interval 等待时长(ms) 默认一分钟
     */
    public unlockMyLockInFuture(interval = CooperationLockManager.UNLOCK_INTERVAL) {
        this.logger.info(`${MODULE_NAME} unlockMyLockInFuture ${interval})`);

        this.clearUnlockTimer();
        this.unlockTimer = setTimeout(() => {
            this.clearUnlockTimer();
            this.unlockMyLock();
        }, interval);
    }

    /**
     * 根据 slide/layout/master index 获取加锁信息
     * @param index
     * @param type
     * @returns
     */
    public getLockInfoByIndex(index: number, type: UIModelType) {
        const slideId = this.getListByType(type)[index]?.id;
        if (!slideId) return;

        return this.getLockInfoById(slideId, type);
    }

    /** 解锁我加的锁 */
    public unlockMyLock() {
        const slideId = this.myLockInfo?.slide.id;
        if (!slideId) return;

        return this.unlockById(slideId);
    }

    private get logger() {
        return console;
    }

    /**
     * 根据 slide/layout/master id 获取加锁信息
     * @param id
     * @param type
     * @returns
     */
    private getLockInfoById(id: string, type: UIModelType): ISlideLock | undefined {
        const lock = this.serverLockMap[id];
        if (!lock) return;

        // slide 数据不存在时（即 slideIndex === -1）也照常返回
        const slideIndex = this.getListByType(type).findIndex(item => item.id === id);
        const userInfo = this.userManager.getUserInfoFromCache(lock.userId);
        return {
            slide: {
                id,
                index: slideIndex,
            },
            user: {
                uid: lock.userId,
                isSelf: lock.userId === this.uid,
                avatar: userInfo?.userPic,
                name: userInfo?.markName,
            },
        };
    }

    /**
     * 根据用户 id 获取用户持有锁信息
     * @param userId 可以是 userId (g.10086) 或 uid (10086)
     */
    private getLockInfoByUid(userId: string): ISlideLock | undefined {
        const uid = getRealUid(userId);
        // 当前一个用户最多持有一个页面锁
        return Object.keys(this.serverLockMap)
            .filter(slideId => this.serverLockMap[slideId]?.userId === uid)
            .map(slideId => this.getLockInfoById(slideId, this.getTypeById(slideId)))[0];
    }

    /** 拉取所有锁信息 */
    private async getAllLockInfo(): Promise<IRequestManagerResponse> {
        return this.requestManager.send('GET_FULL_LOCK', {});
    }

    /** 尝试刷新所有 timer（定时解锁 timer + 轮询 timer）*/
    private refreshAllTimer(hasNewLock: boolean) {
        // 用户锁已删除，清除 timer
        if (!this.myLockInfo) this.clearUnlockTimer();

        // TODO: 不清楚为何无锁时要关闭轮询
        if (Object.keys(this.serverLockMap).length > 0) {
            // 开始轮询, 如果有新lock，则刷新计时器; 因为新lock是通过all lock回来的，所以不需要频繁拉取
            // 参照原逻辑，没有新的锁时，轮询保留旧 timer，不重新计时
            this.startPollingAllLockInfo(!hasNewLock);
        } else {
            this.stopPollingAllLockInfo();
        }
    }

    /**
     * 开始过期锁检测轮询，默认每次调用重新计时
     * @param shouldPreserveOldTimer 是否保留旧 timer，默认 false
     */
    private startPollingAllLockInfo(shouldPreserveOldTimer = false) {
        if (shouldPreserveOldTimer && this.pollingAllLockInfoTimer) return;

        this.stopPollingAllLockInfo();
        const startTime = Date.now();
        let pollingCheckLockInterval = CooperationLockManager.TIMEOUT_LOCK_CHECK_INTERVAL_MIN;
        const polling = () => {
            this.pollingAllLockInfoTimer = setTimeout(() => {
                // 30分钟后降频
                if (Date.now() - startTime > TIME_INTERVAL_30_MIN) {
                    pollingCheckLockInterval = CooperationLockManager.TIMEOUT_LOCK_CHECK_INTERVAL_MAX;
                }
                this.logger.info(`${MODULE_NAME} timeout lock check`);
                this.getAllLockInfo();
                polling();
            }, pollingCheckLockInterval);
        };
        polling();
    }

    /** 停止过期锁检测轮询 */
    private stopPollingAllLockInfo() {
        if (!this.pollingAllLockInfoTimer) return;
        clearTimeout(this.pollingAllLockInfoTimer);
        this.pollingAllLockInfoTimer = null;
    }

    /** 清除定时解锁的timer */
    private clearUnlockTimer() {
        if (!this.unlockTimer) return;

        clearTimeout(this.unlockTimer);
        this.unlockTimer = null;
    }

    /**
     * 通过 type 来返还当前的 list
     * @param type
     * @returns
     */
    private getListByType(type: UIModelType) {
        const { masterList, layoutList, sectionList, slideList } = this.presentationStoreService.getPresentation();
        switch (type) {
            case UIModelType.MASTER:
                return masterList;
            case UIModelType.LAYOUT:
                return layoutList;
            case UIModelType.SECTION:
                return sectionList;
            default:
                return slideList;
        }
    }

    /**
     * 通过 id 来返还当前的 type
     * @param id
     * @returns
     */
    private getTypeById(id: string) {
        const presentation = this.presentationStoreService.getPresentation();
        if (presentation.getMasterById(id)) return UIModelType.MASTER;
        if (presentation.getLayoutById(id)) return UIModelType.LAYOUT;
        return UIModelType.SLIDE;
    }

    private bilateralDiff = <T>(listA: T[], listB: T[]): [T[], T[]] => [
        difference(listA, listB), difference(listB, listA),
    ];

    /**
     * 处理服务端push的加锁信息
     * @param serverLockMap
     */
    private onLock(serverLockMap: ServerLockMap) {
        // 比较新旧两份全量数据计算新增与删除的页面id
        const [addedSlideIds, deletedSlideIds] = this.bilateralDiff(
            Object.keys(serverLockMap),
            Object.keys(this.serverLockMap),
        );

        // 计算新增的用户 id
        const addedUids = difference(
            Object.values(serverLockMap).map(({ userId }) => userId),
            Object.values(this.serverLockMap).map(({ userId }) => userId),
        );

        // 先计算被删除的锁
        const deletedLocks = deletedSlideIds
            .map(slideId => this.getLockInfoById(slideId, this.getTypeById(slideId)))
            .filter((lock): lock is ISlideLock => lock !== undefined);
        // 再更新数据
        this.serverLockMap = serverLockMap;
        this.refreshAllTimer(addedSlideIds.length > 0);

        // 最后再触发事件
        // 此处保留原实现的三个事件，稍有些冗余
        // 'lock' 下发 **全量** 锁信息
        // 'unlock' 下发删除锁信息
        // 'userInfoUpdate' 若有新用户加入编辑，异步拉取用户头像昵称信息后派发 **全量** 锁信息 （包含了用户信息）

        if (deletedLocks.length) {
            this.logger.info(`${MODULE_NAME} receive unlock: ${deletedSlideIds.join(',')}`);
            this.event.emit(EditEvents.Unlock, deletedLocks);
        }

        const { lockInfo } = this;

        if (addedSlideIds.length && lockInfo.length) {
            this.logger.info(`${MODULE_NAME} receive lock: ${addedSlideIds.join(',')}`);
            this.event.emit(EditEvents.Lock, lockInfo);
        }

        if (addedUids.length) {
            this.userManager.getUserInfosFromUids(addedUids).then(() => {
                this.event.emit(EditEvents.LockInfoUpdate, this.lockInfo.slice());
            });
        }
    }

    /**
     * 处理服务端push的解锁信息
     * @param data
     */
    private onUnlock({ pageid: pageId }: { pageid: string }) {
        const lock = this.getLockInfoById(pageId, this.getTypeById(pageId));
        if (!lock) return;

        // 先更新数据
        delete this.serverLockMap[pageId];
        this.refreshAllTimer(false);

        // 日志, 最后再触发事件
        this.logger.info(`${MODULE_NAME} receive unlock: ${pageId}`);
        this.event.emit(EditEvents.Unlock, [lock]);
    }

    // TODO: 有可能会触发两次 unlock
    /** 处理服务端 push 的用户离开信息，为 离开的用户解锁 */
    private onUserLeave({ userId }: { userId: string }) {
        const lock = this.getLockInfoByUid(userId);
        if (!lock) return;

        // 先更新数据
        Reflect.deleteProperty(this.serverLockMap, lock.slide.id);
        this.refreshAllTimer(false);

        // 日志, 最后再触发事件
        this.logger.info(`${MODULE_NAME} receive user leave to unlock: ${lock.slide.id}`);
        this.event.emit(EditEvents.Unlock, [lock]);
    }
}

export default CooperationLockManager;
